// profile/actions.ts
import {ActionTree} from 'vuex';
import {
  ChatPrompt,
  FriendState,
  Meetup,
  meetup,
  message,
  UserWithMessage,
  WebRTCRequest,
  WebRTCRequestState,
} from './types';
import {RootState, geoQuery} from '@/store/types';
import Vue from 'vue';
import * as _ from 'lodash';
import * as firebase from 'firebase/app';
import {MAFUser, UserTransaction} from '@/store/User/types';
import {PlaceDetailsResult} from '@google/maps';
import QuerySnapshot = firebase.firestore.QuerySnapshot;
import QueryDocumentSnapshot = firebase.firestore.QueryDocumentSnapshot;

/* tslint:disable:no-console */

export const actions: ActionTree<FriendState, RootState> = {
  async getFriends({state, commit}): Promise<any> {
    Vue.prototype.$firestore.collection(`users`).
      where('signUpPhase', '==', -1).onSnapshot(async (snapshot: QuerySnapshot) => {

      const users: MAFUser[] = [];
      for (const doc of snapshot.docs) {
        const mafUser = new MAFUser(doc.data());
        mafUser.setDocumentReference(doc.ref);
        // @ts-ignore
        if (mafUser.uid !== this.state.currentUser.uid) {
          try {
            const url: string = await mafUser.getImageUrl(Vue.prototype);
            mafUser.imageURL = url;
          } catch (e) {
            console.error(e);
          }
          users.push(mafUser);
        }
      }

      commit('setFriends', users);
    }, (error: any) => {
      if (Vue.prototype.$auth.currentUser !== null) {
        console.log(`Error getting friends: ${error}`);
      }
    });
  },

  async getFriendsNearMe({state, commit}, {position, radius}): Promise<any> {
    return new Promise<any>((resolve) => {
      const users: MAFUser[] = [];
      const geoQ = new geoQuery();
      const qs = geoQ.getQueriesForDocumentsAround(Vue.prototype.$firestore.collection('users'), position, radius);
      const done = _.after(qs.length, () => {
        resolve(users);
      });
      qs.forEach(async (q: any) => {
        const qss = await q.get();
        if (!qss || !qss.docs || !qss.docs.length) {
          done();
        }
        const qsDone = _.after(qss.docs.length, () => {
          done();
        });
        qss.forEach(async (doc: any) => {
          const u = new MAFUser(doc.data());
          u.setDocumentReference(doc.ref);
          const exists = _.filter(users, {uid: u.uid});
          if (!exists || !exists.length) {
            // @ts-ignore
            if (u.uid !== this.state.currentUser.uid) {
              u.imageURL = await u.getImageUrl(Vue.prototype);
              users.push(u);
            }
          }
          qsDone();
        });
      });
    });
  },

  async getMessages({state, commit}, uid: string) {
    Vue.prototype.$firestore.collection(`users/${uid}/messages`).onSnapshot((snapshot: QuerySnapshot) => {
      const messages: message[] = [];
      let ret: UserWithMessage[] = [];
      const messageDone = _.after(snapshot.docs.length, () => {
        const grouped = _.groupBy(messages, (m) => {
          const mes = m.from === uid ? m.to : m.from;
          return mes;
        });
        const done = _.after(Object.keys(grouped).length, () => {
          ret = _.orderBy(ret, ['messages[0].sent'], ['desc']);
          commit('setMessages', ret);
        });
        _.forEach(grouped, async (value, key) => {
          if (value && value.length && value[0].userRef) {
            value[0].userRef.get().then((document) => {
              const user = new MAFUser(document.data());
              user.getImageUrl(Vue.prototype).then((url) => {
                user.imageURL = url;
                const mes = _.orderBy(value, ['sent'], ['desc']);
                ret.push(new UserWithMessage(user, mes));
                done();
              });
            }).catch((e) => {
              done();
            });
          }
        });
      });
      _.forEach(snapshot.docs, (document: QueryDocumentSnapshot): any => {
        const mes = new message(
          document.data().to,
          document.data().from,
          document.data().sent,
          document.data().message,
          document.data().userRef,
          document.data().isMeetupNotification,
          document.data().imgRef,
          document.data().prompt,
        );
        if (mes.imgRef) {
          mes.getImageUrl(Vue.prototype).then( (url) => {
            mes.ImgRef = url;
            messages.push(mes);
            messageDone();
          });
        } else {
          messages.push(mes);
          messageDone();
        }
      });
    }, (error: any) => {
      if (Vue.prototype.$auth.currentUser !== null) {
        console.log(`Error getting messages: ${error}`);
      }
    });
  },

  async getMyMeetups({state, commit}, uid: string): Promise<any> {
    Vue.prototype.$firestore.collection(`users/${uid}/meetups`).onSnapshot((snapshot: QuerySnapshot) => {
      const meetups: Meetup[] = [];
      const done = _.after(snapshot.docs.length, () => {
        commit('setMeetups', meetups);
      });
      _.forEach(snapshot.docs, (document: QueryDocumentSnapshot) => {
        const mu: Meetup = new Meetup(document.data());
        mu.id = document.id;
        mu.getUsers().then(() => {
          meetups.push(mu);
          done();
        });
      });
    }, (error: any) => {
      if (Vue.prototype.$auth.currentUser !== null) {
        console.log(`Error getting meetups: ${error}`);
      }
    });
  },

  async createMeetup({state, commit}, mu: meetup): Promise<any> {
    return await Vue.prototype.$firestore.collection(`users/${mu.inviter}/meetups`).add(mu);
  },

  async updateMeetup({state, commit}, {mu, mid, uid}): Promise<any> {
    return await Vue.prototype.$firestore.
      collection(`users/${uid}/meetups`).doc(mid).set(mu.toPlainObj(), {merge: true});
  },

  async createMessage({state, commit}, mes: message): Promise<any> {
    return await Vue.prototype.$firestore.collection(`users/${mes.from}/messages`).add(mes);
    // return await Vue.prototype.$firestore.collection(`users/${mes.to}/messages`).add(mes);
  },

  async getPlaces({state, commit}, data: any): Promise<PlaceDetailsResult[]> {
    const {query, lat, lng, radius = 10} = data;
    const findPlaces = Vue.prototype.$functions.httpsCallable('queryGooglePlaces');
    const res = await findPlaces({query, lat, lng, radius});
    console.log(res);
    return res.data;
  },

  getFriendTransactions({commit}, uid): any {
    try {
        Vue.prototype.$firestore.
            collection(`users/${uid}/transactions`).onSnapshot((snapshot: QuerySnapshot | any ) => {

            const doc = UserTransaction.getListOfUserTransaction(snapshot);
            if (doc.length) {
                commit('setFriendTransactions', doc);
            }
        }, (error: any) => {
          if (Vue.prototype.$auth.currentUser !== null) {
            console.log(`Error getting transactions: ${error}`);
          }
        });
    } catch (e) {
        console.log('Error getting document:', e);
    }
  },

  async giftCoinsToFriend({state, commit}, {me, friend, gift}): Promise<any> {
    me.coins = me.coins - (parseInt(gift, 10));
    friend.coins = friend.coins + (parseInt(gift, 10));

    try {
      // update their coins
      const friendDocumentRef = Vue.prototype.$firestore.collection('users').doc(friend.uid);
      friend.comparison = undefined;
      friend.Reference = undefined;
      friendDocumentRef.set(friend.toPlainObj(), {merge: true});

      // update my coins
      // const myDocumentRef = Vue.prototype.$firestore.collection('users').doc(me.uid);
      // me.comparison = undefined;
      // me.Reference = undefined;
      // myDocumentRef.set(me.toPlainObj(), {merge: true});

      // set up purchase
      const what = `gift${gift}`;
      const purchase = Vue.prototype.$functions.httpsCallable('purchaseCoins');
      const res = await purchase({what});
    } catch (error) {
      console.error(error);
    }
  },

  async sendInvite({commit, state}, to: string): Promise<any> {
    const sendInvite = Vue.prototype.$functions.httpsCallable('sendInvite');
    try {
      const res = await sendInvite({to});
      return {res: res.data};
    } catch (e) {
      return {err: e};
    }
  },

  async getChatPrompts({commit, state}) {
    const doc = await Vue.prototype.$firestore.collection('lookups').doc('chat-prompts').get();
    if (doc.exists) {
      commit('setChatPrompt', new ChatPrompt(doc.data()));
    }
  },

  async sendP2PConnection({commit}, data: WebRTCRequest): Promise<any> {
    if (!data.Reference) {
      const doc = await Vue.prototype.$firestore.collection(`p2pconnections`).add(data.toPlainObj());
    } else {
      await data.Reference.set(data.toPlainObj(), {merge: true});
    }
    return;
  },
/* tslint:disable:max-line-length */
  async sendEmailNotification({commit}, {to, mess}): Promise<any> {
    const sendEmailFromFirestore = Vue.prototype.$functions.httpsCallable('sendEmail');

    try {
      await sendEmailFromFirestore({to, mess});
    } catch (e) {
      console.error(e);
    }
  },

  async purchaseCoins({commit}, {body, me}): Promise<any> {
    const purchaseCoinsFromFirestore = Vue.prototype.$functions.httpsCallable('createCharge');
    const what = `buy${+body.coins.coins}`;

    // prep for updating my coins
    const myDocumentRef = Vue.prototype.$firestore.collection('users').doc(me.uid);
    me.comparison = undefined;
    me.Reference = undefined;
    me.coins += +body.coins.coins;

    try {
      await purchaseCoinsFromFirestore(body);
      myDocumentRef.set(me.toPlainObj(), {merge: true});

      const purchase = Vue.prototype.$functions.httpsCallable('purchaseCoins');
      const res = await purchase({what});
    } catch (e) {
      console.error(e);
    }
  },

  // watching for incoming connections
  watchForConnections({commit}, uid: string) {
    Vue.prototype.$firestore.collection('p2pconnections')
      .where('to', '==', uid).onSnapshot((snapshot: QuerySnapshot) => {
        const ret: WebRTCRequest[] = [];
        const done = _.after(snapshot.docs.length, () => {
          _.remove(ret, {state: WebRTCRequestState.Ended});
          commit('setToWebRequest', ret);
        });
        snapshot.forEach((doc: QueryDocumentSnapshot) => {
          const newWebRTC = new WebRTCRequest(doc.data(), null, null, null, undefined, null, false, false);
          newWebRTC.getUsers(Vue.prototype.$firestore).then(() => {
            newWebRTC.setDocumentReference(doc.ref);
            ret.push(newWebRTC);
            done();
          });
        });
    }, (error: any) => {
      if (Vue.prototype.$auth.currentUser !== null) {
        console.log(`Error getting p2pconnections: ${error}`);
      }
    });
  },

  watchForYourConnections({commit}, uid: string) {
    Vue.prototype.$firestore.collection('p2pconnections')
      .where('sender', '==', uid).onSnapshot((snapshot: QuerySnapshot) => {
      const ret: WebRTCRequest[] = [];
      const done = _.after(snapshot.docs.length, () => {
          _.remove(ret, {state: WebRTCRequestState.Ended});
          commit('setSenderWebRequest', ret);
      });
      snapshot.forEach((doc: QueryDocumentSnapshot) => {
        const newWebRTC = new WebRTCRequest(doc.data(), null, null, null, undefined, null, false, false);
        newWebRTC.setDocumentReference(doc.ref);
        newWebRTC.getUsers(Vue.prototype.$firestore).then(() => {
            ret.push(newWebRTC);
            done();
        });
      });
    }, (error: any) => {
      if (Vue.prototype.$auth.currentUser !== null) {
        console.log(`Error getting p2pconnections: ${error}`);
      }
    });
  },
};
